import React, { useState, useEffect, useContext, useMemo } from 'react';
import { AppContext } from '../../App';
import licenseService from '../../services/licenseService';
import { getApiUrl } from '../../utils';
import { trackEvent } from '../../analytics';
import { Download, Key, Loader2, AlertCircle, CheckCircle, Wifi, WifiOff, Info, Server } from 'lucide-react';

// Parse a challenge blob and extract its contents
function parseChallengeBlob(blob) {
  if (!blob || !blob.trim()) return null;
  try {
    const decoded = atob(blob.trim());
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

export default function GrantIssuanceForm({ customer, onGrantIssued }) {
  const { selectedTeam } = useContext(AppContext);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [lastGrant, setLastGrant] = useState(null);

  const [formData, setFormData] = useState({
    productId: '',
    seats: 10,
    validUntil: getDefaultExpiry(),
    notes: '',
    challengeBlob: '',
    applianceNickname: ''
  });

  // Parse challenge whenever it changes
  const parsedChallenge = useMemo(
    () => parseChallengeBlob(formData.challengeBlob),
    [formData.challengeBlob]
  );

  // Auto-suggest nickname from challenge instanceId
  useEffect(() => {
    if (parsedChallenge?.instanceId && !formData.applianceNickname) {
      setFormData(prev => ({ ...prev, applianceNickname: parsedChallenge.instanceId }));
    }
  }, [parsedChallenge?.instanceId]);

  function getDefaultExpiry() {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    return date.toISOString().split('T')[0];
  }

  useEffect(() => {
    const loadProducts = async () => {
      if (!selectedTeam) return;
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${getApiUrl()}/api/products?teamId=${selectedTeam.teamId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success) {
          setProducts(data.products || []);
          if (data.products?.length > 0 && !formData.productId) {
            setFormData(prev => ({ ...prev, productId: data.products[0].id }));
          }
        }
      } catch (err) {
        console.error('Failed to load products:', err);
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, [selectedTeam]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value, 10) || 0 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await licenseService.issueGrant({
        customerProxyTeamId: customer.customer_proxy_team_id,
        productId: formData.productId,
        seats: formData.seats,
        validUntil: new Date(formData.validUntil).toISOString(),
        notes: formData.notes || undefined,
        challengeBlob: formData.challengeBlob || undefined,
        applianceNickname: formData.applianceNickname || undefined
      });

      if (response.success) {
        trackEvent('issue_grant', {
          seats: Number(formData.seats) || 0,
          offline: Boolean(formData.challengeBlob)
        });
        setLastGrant(response);
        setSuccess('License grant issued successfully!');
        setFormData(prev => ({ ...prev, notes: '', challengeBlob: '', applianceNickname: '' }));
        if (onGrantIssued) onGrantIssued();
      } else {
        setError(response.error || 'Failed to issue grant');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = () => {
    if (lastGrant?.signedBlob && lastGrant?.downloadFilename) {
      licenseService.downloadGrantFile(lastGrant.signedBlob, lastGrant.downloadFilename);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-400">Loading products...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Product</label>
            <select
              name="productId"
              value={formData.productId}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select a product</option>
              {products.map(product => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Seats (Max Active Keys)</label>
            <input
              type="number"
              name="seats"
              value={formData.seats}
              onChange={handleChange}
              min="1"
              max="10000"
              required
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Valid Until</label>
            <input
              type="date"
              name="validUntil"
              value={formData.validUntil}
              onChange={handleChange}
              required
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Notes (Optional)</label>
            <input
              type="text"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Internal notes about this grant"
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="border border-gray-600 rounded-lg p-4 bg-gray-750">
          <div className="flex items-center gap-4 mb-4">
            <span className="text-sm font-medium">Deployment Type:</span>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="deploymentType"
                checked={!formData.challengeBlob}
                onChange={() => setFormData(prev => ({ ...prev, challengeBlob: '' }))}
                className="text-blue-500"
              />
              <Wifi className="w-4 h-4 text-green-400" />
              <span className="text-sm">Online</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="deploymentType"
                checked={!!formData.challengeBlob}
                onChange={() => document.getElementById('challengeBlob')?.focus()}
                className="text-blue-500"
              />
              <WifiOff className="w-4 h-4 text-orange-400" />
              <span className="text-sm">Offline / Air-gapped</span>
            </label>
          </div>

          {!formData.challengeBlob ? (
            <div className="text-sm text-gray-400 bg-gray-700/50 rounded p-3">
              <p className="flex items-center gap-2">
                <Wifi className="w-4 h-4 text-green-400" />
                <strong className="text-gray-300">Online Mode:</strong>
              </p>
              <p className="mt-1 ml-6">
                License will be validated via API. Customer needs internet connectivity to validate API keys.
              </p>
            </div>
          ) : null}

          <div className={formData.challengeBlob ? '' : 'mt-4'}>
            <label className="block text-sm font-medium mb-2">
              <WifiOff className="w-4 h-4 inline mr-2 text-orange-400" />
              Offline Activation Challenge
            </label>
            <textarea
              id="challengeBlob"
              name="challengeBlob"
              value={formData.challengeBlob}
              onChange={handleChange}
              placeholder="Paste the challenge blob from the customer's License Manager..."
              rows={3}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
            />
            <p className="text-xs text-gray-400 mt-2">
              For air-gapped deployments: The customer generates this challenge on their License Manager.
              Each grant requires a fresh challenge (challenges cannot be reused).
            </p>

            {/* Challenge Details Display */}
            {parsedChallenge && (
              <div className="mt-3 p-3 bg-gray-700/50 rounded-lg border border-gray-600">
                <div className="flex items-center gap-2 mb-2">
                  <Info className="w-4 h-4 text-blue-400" />
                  <span className="text-sm font-medium text-gray-300">Challenge Details</span>
                </div>
                <div className="text-xs space-y-1 text-gray-400">
                  {parsedChallenge.instanceId && (
                    <p>
                      <span className="text-gray-500">Instance:</span>{' '}
                      <span className="font-mono text-gray-300">{parsedChallenge.instanceId}</span>
                    </p>
                  )}
                  {parsedChallenge.hwHash && (
                    <p>
                      <span className="text-gray-500">Hardware:</span>{' '}
                      <span className="font-mono text-gray-300">{parsedChallenge.hwHash.slice(0, 16)}...</span>
                    </p>
                  )}
                  {parsedChallenge.reqSeats && (
                    <p>
                      <span className="text-gray-500">Requested Seats:</span>{' '}
                      <span className="text-gray-300">{parsedChallenge.reqSeats}</span>
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Appliance Nickname */}
            {parsedChallenge && (
              <div className="mt-3">
                <label className="block text-sm font-medium mb-2">
                  <Server className="w-4 h-4 inline mr-2 text-gray-400" />
                  Appliance Nickname
                </label>
                <input
                  type="text"
                  name="applianceNickname"
                  value={formData.applianceNickname}
                  onChange={handleChange}
                  placeholder="e.g. Factory Floor Server, Lab #3"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-400 mt-1">
                  A friendly name to identify this deployment in the dashboard.
                </p>
              </div>
            )}

            {/* Invalid Challenge Warning */}
            {formData.challengeBlob && !parsedChallenge && (
              <div className="mt-3 flex items-center p-3 bg-yellow-900/30 border border-yellow-500 rounded-lg">
                <AlertCircle className="w-5 h-5 text-yellow-500 mr-2" />
                <span className="text-sm text-yellow-200">
                  Invalid challenge format. Please paste the complete challenge blob.
                </span>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="flex items-center p-4 bg-red-900/30 border border-red-500 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-500 mr-3" />
            <span className="text-red-200">{error}</span>
          </div>
        )}

        {success && (
          <div className="flex items-center p-4 bg-green-900/30 border border-green-500 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
            <span className="text-green-200">{success}</span>
          </div>
        )}

        <div className="flex gap-4">
          <button
            type="submit"
            disabled={submitting || !formData.productId}
            className={`
              flex items-center px-6 py-3 rounded-lg font-medium transition-colors
              ${submitting || !formData.productId
                ? 'bg-gray-600 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
              }
            `}
          >
            {submitting ? (
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
            ) : (
              <Key className="w-5 h-5 mr-2" />
            )}
            Issue License Grant
          </button>

          {lastGrant && (
            <button
              type="button"
              onClick={handleDownload}
              className="flex items-center px-6 py-3 bg-green-600 hover:bg-green-700 rounded-lg font-medium transition-colors"
            >
              <Download className="w-5 h-5 mr-2" />
              Download .rigrlic File
            </button>
          )}
        </div>
      </form>

      {lastGrant && (
        <div className="bg-gray-700/50 rounded-lg p-4">
          <h3 className="font-medium mb-2">Last Issued Grant</h3>
          <div className="text-sm text-gray-300 space-y-1">
            <p><span className="text-gray-400">Grant ID:</span> {lastGrant.grantId}</p>
            <p><span className="text-gray-400">Product:</span> {lastGrant.payload?.productId}</p>
            <p><span className="text-gray-400">Seats:</span> {lastGrant.payload?.entitlements?.max_active_keys}</p>
            <p><span className="text-gray-400">Valid Until:</span> {new Date(lastGrant.payload?.validUntil).toLocaleDateString()}</p>
          </div>
        </div>
      )}
    </div>
  );
}
